/**
 * Tracks playback events for TTFA (Time to First Audio) and WLH (Weekly Listening Hours)
 */

import mixpanelService from '../mixpanel-service'
import { generateUUID } from '@utils/uuid'
import {
    EVENTS,
    PROPS,
    CONTENT_TYPES,
    PLAYER_STATES,
    ATTRIBUTION_SOURCES,
    HEARTBEAT_INTERVAL_SECONDS
} from './analytics-spec'

class PlaybackTracker {
    constructor() {
        if (PlaybackTracker.instance) {
            return PlaybackTracker.instance
        }
        PlaybackTracker.instance = this

        this._session = null
        this._lastStateChangeTime = null
    }

    /**
     * Start a new playback session - ends existing session if active
     */
    startSession({ contentType, contentId, sourceScreen, attributionSource, radioChannelName }) {
        if (this._session?.audioStartedFired) {
            this.endSession('switch_content')
        }

        const requestTimestamp = performance.now()

        this._session = {
            playbackRequestId: generateUUID(),
            contentType,
            contentId,
            sourceScreen,
            attributionSource: attributionSource || ATTRIBUTION_SOURCES.UNKNOWN,
            radioChannelName,
            requestTimestamp,
            audioStartedFired: false,
            ended: false,
            playerState: null,
            isBackground: false,
            accumulatedListeningMs: 0,
            lastHeartbeatMs: 0,
            listeningSecondsCumulative: 0,
            playheadSec: null
        }

        this._lastStateChangeTime = requestTimestamp

        const props = {
            [PROPS.PLAYBACK_REQUEST_ID]: this._session.playbackRequestId,
            [PROPS.CONTENT_TYPE]: contentType,
            [PROPS.CONTENT_ID]: contentId,
            [PROPS.SOURCE_SCREEN]: sourceScreen,
            [PROPS.ATTRIBUTION_SOURCE]: this._session.attributionSource
        }

        if (radioChannelName) {
            props[PROPS.RADIO_CHANNEL_NAME] = radioChannelName
        }

        mixpanelService.track(EVENTS.PLAYBACK_REQUESTED, props)
    }

    /**
     * Mark that audio has started playing - fires audio_started with TTFA
     */
    onAudioStarted() {
        if (!this._session || this._session.audioStartedFired) return

        this._session.audioStartedFired = true
        const ttAudioStartedMs = Math.round(performance.now() - this._session.requestTimestamp)

        mixpanelService.track(EVENTS.AUDIO_STARTED, {
            [PROPS.PLAYBACK_REQUEST_ID]: this._session.playbackRequestId,
            [PROPS.CONTENT_TYPE]: this._session.contentType,
            [PROPS.CONTENT_ID]: this._session.contentId,
            [PROPS.TT_AUDIO_STARTED_MS]: ttAudioStartedMs,
            [PROPS.ATTRIBUTION_SOURCE]: this._session.attributionSource
        })
    }

    /**
     * Update player state - accumulates listening time when state is 'playing'
     */
    updatePlayerState(state) {
        if (!this._session) return

        const now = performance.now()

        // Accumulate time if previous state was playing
        if (this._session.playerState === PLAYER_STATES.PLAYING) {
            this._session.accumulatedListeningMs += now - this._lastStateChangeTime
        }

        this._session.playerState = state
        this._lastStateChangeTime = now

        this._checkHeartbeatThreshold()
    }

    /**
     * Update playhead position (for on-demand content)
     */
    updatePlayhead(seconds) {
        if (this._session) {
            this._session.playheadSec = seconds
        }
    }

    _checkHeartbeatThreshold() {
        if (!this._session) return

        const thresholdMs = HEARTBEAT_INTERVAL_SECONDS * 1000
        const listeningMsSinceLastHeartbeat = this._session.accumulatedListeningMs - this._session.lastHeartbeatMs

        if (listeningMsSinceLastHeartbeat >= thresholdMs) {
            this._emitHeartbeat()
        }
    }

    _emitHeartbeat() {
        if (!this._session) return

        const deltaListeningSeconds = Math.round(
            (this._session.accumulatedListeningMs - this._session.lastHeartbeatMs) / 1000
        )

        if (deltaListeningSeconds <= 0) return

        this._session.listeningSecondsCumulative += deltaListeningSeconds
        this._session.lastHeartbeatMs = this._session.accumulatedListeningMs

        mixpanelService.track(EVENTS.PLAYBACK_HEARTBEAT, {
            [PROPS.PLAYBACK_REQUEST_ID]: this._session.playbackRequestId,
            [PROPS.CONTENT_TYPE]: this._session.contentType,
            [PROPS.CONTENT_ID]: this._session.contentId,
            [PROPS.DELTA_LISTENING_SECONDS]: deltaListeningSeconds,
            [PROPS.LISTENING_SECONDS_CUMULATIVE]: this._session.listeningSecondsCumulative,
            [PROPS.PLAYHEAD_SEC]: this._session.contentType === CONTENT_TYPES.RADIO ? null : this._session.playheadSec,
            [PROPS.IS_BACKGROUND]: this._session.isBackground,
            [PROPS.PLAYER_STATE]: this._session.playerState || PLAYER_STATES.PLAYING,
            [PROPS.ATTRIBUTION_SOURCE]: this._session.attributionSource
        })
    }

    /**
     * Flush accumulated listening time as heartbeat - used for visibility change and pagehide
     */
    flush() {
        if (!this._session) return

        this._accumulateCurrentTime()

        if (this._session.accumulatedListeningMs > this._session.lastHeartbeatMs) {
            this._emitHeartbeat()
        }
    }

    /**
     * Handle visibility change (tab hidden/visible)
     */
    onVisibilityChange(isHidden) {
        if (!this._session) return

        if (isHidden && this._session.playerState === PLAYER_STATES.PLAYING) {
            this._accumulateCurrentTime()
        }

        this._session.isBackground = isHidden

        if (isHidden) {
            this._emitHeartbeat()
        }
    }

    /**
     * Handle pagehide event - flushes heartbeat and ends session
     */
    onPageHide() {
        if (!this._session) return

        this._accumulateCurrentTime()

        if (this._session.accumulatedListeningMs > this._session.lastHeartbeatMs) {
            this._emitHeartbeat()
        }

        this.endSession('app_background')
    }

    /**
     * End the current playback session
     */
    endSession(endedReason) {
        if (!this._session || this._session.ended) return

        this._session.ended = true

        mixpanelService.track(EVENTS.PLAYBACK_ENDED, {
            [PROPS.PLAYBACK_REQUEST_ID]: this._session.playbackRequestId,
            [PROPS.CONTENT_TYPE]: this._session.contentType,
            [PROPS.CONTENT_ID]: this._session.contentId,
            [PROPS.ENDED_REASON]: endedReason,
            [PROPS.LISTENING_SECONDS_CUMULATIVE]: this._session.listeningSecondsCumulative,
            [PROPS.ATTRIBUTION_SOURCE]: this._session.attributionSource
        })

        this._session = null
    }

    /**
     * Track a playback error
     */
    onPlaybackError(stage, code, message) {
        if (!this._session) return

        mixpanelService.track(EVENTS.PLAYBACK_ERROR, {
            [PROPS.PLAYBACK_REQUEST_ID]: this._session.playbackRequestId,
            [PROPS.CONTENT_TYPE]: this._session.contentType,
            [PROPS.CONTENT_ID]: this._session.contentId,
            [PROPS.ERROR_STAGE]: stage,
            [PROPS.ERROR_CODE]: code,
            [PROPS.ERROR_MESSAGE]: message,
            [PROPS.ATTRIBUTION_SOURCE]: this._session.attributionSource
        })
    }

    // Helper to accumulate time if currently playing
    _accumulateCurrentTime() {
        if (this._session?.playerState === PLAYER_STATES.PLAYING) {
            const now = performance.now()
            this._session.accumulatedListeningMs += now - this._lastStateChangeTime
            this._lastStateChangeTime = now
        }
    }

    getCurrentSession() {
        return this._session
    }

    _resetForTesting() {
        this._session = null
        this._lastStateChangeTime = null
    }
}

export default new PlaybackTracker()
